import { Button, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import MuiInput from "@shared/ui/mui-input/MuiInput";
import { StyledEditBank } from "./EditBank.styled";
import { useContext, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { AccountEdit, AccountSchema } from "@models/account";
import { deleteEmptyFieldsFromObject, groupBy } from "@shared/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";
import { AccountContext } from "@features/tickets/shared/contexts/AccountContext";
import AlertDialog from "@shared/ui/alert-dialog/AlertDialog";
import { setCustomDataArray } from "@features/tickets/shared/utils/customData";
import { useLocation, useNavigate } from "react-router-dom";
import SettingsIcon from '@mui/icons-material/Settings';
import { useTheme } from "@emotion/react";
import { getToastErrorMessage } from "@shared/lib/errors"
import { useGetAccountStructureQuery } from "@features/structures/api/accountsStructure.api";
import ReturnButton from "@shared/ui/navigation/ReturnButton";
import { editLastBread } from "@features/tickets/shared/utils/breadcrumbs";
import { useDeleteAccountMutation, useSaveAccountMutation } from "@features/tickets/api/accounts.api";


function EditBank() {

  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: bankStructure } = useGetAccountStructureQuery("בנק");
  
  const structureSortedByTitle = groupBy(bankStructure, "account_title")
  const customFieldsExist = structureSortedByTitle?.["מותאם אישית"]?.find(structure => !!structure.show_form === true);


  const { account, setAccount } = useContext(AccountContext);
  const [saveAccount] = useSaveAccountMutation();
  const [deleteAccount] = useDeleteAccountMutation();


  const [dialogIsOpen, setDialogIsOpen] = useState(false);

  const form = useForm<AccountEdit>({
    resolver: zodResolver(AccountSchema),
    defaultValues: {
      ...deleteEmptyFieldsFromObject(account)
    }
  });


  useEffect(() => {
    if (account) {
      form.reset(account);
    }
  }, [account]);


  const saveTicket = async () => {

    try {

      const customDataArray = form.getValues("custom_data");
      setCustomDataArray(customDataArray);
      form.setValue("custom_data", customDataArray);

      form.handleSubmit(onSubmit)();


    } catch (error) {
      console.error("Form submission failed:", error);
    }

  }


  const onSubmit: SubmitHandler<AccountEdit> = async (data) => {


    // const dataToSend = deleteEmptyFieldsFromObject(data);

    const savePromise = async () => {

      const savedBank = await saveAccount({
        accountId: account!.id,
        accountToSave: data,
        accountType: "בנק",
      }).unwrap();
      setAccount(savedBank);

    }

    await toast.promise(
      savePromise,
      {
        pending: 'מעדכן כרטיס...',
        success: 'הכרטיס עודכן בהצלחה',
        error: {
          render({ data }) {
            return getToastErrorMessage(data)
          }
        }
      }
    )
  };

  // function returnFunc() {
  //   window.history.back();
  // }

  const onDelete = async () => {

    const deletePromise = async () => {
      if (account) {
        await deleteAccount({ accountId: account.id!, accountType: "בנק" }).unwrap();
        window.history.back();
      }
    }

    toast.promise(
      deletePromise,
      {
        pending: 'מוחק כרטיס...',
        success: 'הכרטיס נמחק בהצלחה',
        error: {
          render({ data }) {
            // When the promise reject, data will contains the error
            return getToastErrorMessage(data)
          }
        }
      }
    )
  };

  function createState(){
    return {
        ...location.state,
    }
  }
  
  function createBreadcrumbData(state: any){

    const breadcrumbData = [
      {
        name: "כרטסות",
      },
      {
        name: "בנקים",
        link: "/banks",
      },
      {
        name: "עריכת כרטיס",
        link: `/banks/${account?.id}`
      },
    ];

    return [...(editLastBread(state?.breadcrumbData, `/banks/${account?.id}`, state) ?? breadcrumbData)];
  }

  function setStructure() {

    const currentState = createState();
    const breadcrumbData = createBreadcrumbData(currentState);

    navigate('/banks/structure/form', {
      replace: true,
      state: {
        ...currentState,
        breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה" }]
      } 
    });
  }

  return (
    <StyledEditBank>
      {!bankStructure ? <Skeleton variant="rectangular" height={500} animation="wave" sx={{ bgcolor: theme.colors.section_color }} /> :
        <>
          <div className="form">
            <div>
              <div className="left">
                {/* <button onClick={setStructure}>הגדרת שדות</button> */}
                <Tooltip title="הגדרת שדות">
                  <IconButton onClick={setStructure} sx={{ color: theme.colors.main_color }}>
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            <div>
              <Typography variant="body1">פרטים:</Typography>
              <div className="fields">
                {structureSortedByTitle?.["פרטים"]?.map(field => {
                  return field.show_form ? <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable /> : null
                })}
              </div>
            </div>

            <div>
              {customFieldsExist && (<Typography variant="body1">שדות מותאמים אישית:</Typography>)}
              <div className="fields">
                {structureSortedByTitle?.["מותאם אישית"]?.map(field => {
                  return field.show_form ? <MuiInput key={field.field_name} field={field} variant='outlined' useForm={form} showRequired blockNotEditable /> : null
                })}
              </div>
            </div>

          </div>

          <div className="buttons">
            <div className="right">
              <Button variant="contained" onClick={saveTicket} disabled={form.formState.isSubmitting} >שמירה</Button>
              {/* <Button variant="outlined" onClick={returnFunc}>חזור</Button> */}
              <ReturnButton/>
              <Button variant="outlined" color="error" onClick={() => setDialogIsOpen(true)} disabled={account?.entries?.length ? true : false} >מחיקה</Button>
            </div>
            <div className="left">
            </div>
          </div>

        </>
      }
      <AlertDialog
        open={dialogIsOpen}
        handleClose={() => { setDialogIsOpen(false) }}
        handleConfirm={onDelete}
        title="אישור מחיקה"
        message="אתה בטוח שאתה רוצה למחוק כרטיס זה?"
        confirm="מחיקה"
      />
    </StyledEditBank>
  )
}


export default EditBank